import type { FrameGeometry, Rect, Story, TextFrameItem } from '@proofroom/schema';
import { storyFrames, walkItems, type SnapshotIndex } from './snapshotIndex';

export function intersects(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

export function expand(r: Rect, offsets: [number, number, number, number]): Rect {
  const [t, l, b, rr] = offsets;
  return { x: r.x - l, y: r.y - t, w: r.w + l + rr, h: r.h + t + b };
}

/** Exclusion rects from other frames on the same spread that wrap text around themselves. */
export function wrapExclusions(frame: TextFrameItem, idx: SnapshotIndex): Rect[] {
  const si = idx.itemSpread[frame.id];
  const spread = idx.snapshot.spreads[si];
  if (!spread) return [];
  const out: Rect[] = [];
  walkItems(spread.items, (item) => {
    if (item.id === frame.id || item.kind !== 'text' || !item.wrap || item.wrap.mode === 'none') return;
    if (item.storyId === frame.storyId) return;
    const w = item.wrap;
    let rect: Rect;
    if (w.mode === 'contour' && w.contours && w.contours.length > 0) {
      const xs = w.contours.flat().map((p) => p[0]);
      const ys = w.contours.flat().map((p) => p[1]);
      rect = { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
    } else if (w.mode === 'jump' || w.mode === 'nextColumn') {
      rect = expand({ x: frame.bounds.x, y: item.bounds.y, w: frame.bounds.w, h: item.bounds.h }, w.offsets);
    } else {
      rect = expand(item.bounds, w.offsets);
    }
    if (intersects(rect, frame.bounds)) out.push(rect);
  });
  return out;
}

export function frameGeometry(frame: TextFrameItem, idx?: SnapshotIndex): FrameGeometry {
  const geo: FrameGeometry = {
    frameId: frame.id,
    bounds: { ...frame.bounds },
    columns: { count: Math.max(1, frame.columns?.count ?? 1), gutter: frame.columns?.gutter ?? 0, ...(frame.columns?.widths ? { widths: [...frame.columns.widths] } : {}) },
    insets: [...(frame.insets ?? [0, 0, 0, 0])] as [number, number, number, number],
    verticalAlign: frame.verticalAlign ?? 'top',
    firstBaseline: frame.firstBaseline ?? 'ascent',
    firstBaselineMin: frame.firstBaselineMin ?? 0,
  };
  if (frame.wrap) geo.wrap = frame.wrap;
  if (idx) {
    const ex = wrapExclusions(frame, idx);
    if (ex.length) geo.exclusions = ex;
  }
  return geo;
}

/** Frame chain geometry for a story, in thread order. */
export function storyGeometry(story: Story, idx: SnapshotIndex): FrameGeometry[] {
  return storyFrames(story, idx).map((f) => frameGeometry(f, idx));
}
